import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import providerSchema from "modules/schemas/providerSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import "styles/sf-base.scss";
import "styles/sf-dropdown.scss";
import IRegion from "interfaces/IRegion";
import IAddProviderRequestModel from "interfaces/Request/IAddProviderRequestModel";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddProvider = () => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IAddProviderRequestModel>({
    resolver: yupResolver(providerSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addProviderIsSending,
    errors: addProviderErrors,
    statusCode: addProviderStatusCode,
    sendRequest: addProviderSendRequest,
    data: addProviderResponseData
  } = useAmplifyREST();

  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const isSending = getRegionsIsSending || addProviderIsSending || !aaFixDropDownTreeCompleted;

  const navigate = useNavigate();

  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>();
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const regionFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: true };

  const canFormBeSubmitted = () => {
    setAddButtonDisabled(
      !(
        getValues("name").length > 0
        && getValues("type").length > 0
        && getValues("emailAddress").length > 0
        && selectedRegions
        && selectedRegions.length > 0
      )
    );
  };

  const watchFields = watch([
    "name",
    "type",
    "emailAddress",
    "notes",
    "active"
  ]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedRegions]);

  const dropDownTreeComponentOnChange = () => {
    setSelectedRegions(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    if (getRegionsErrors) {
      setApiErrors(current => [...current, ...getRegionsErrors]);
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [getRegionsErrors, setApiErrors, setShowExceptions]);

  const handleAddProviderClick = (request: IAddProviderRequestModel) => {
    setApiErrors([]);
    request.regions = selectedRegions!;
    addProviderSendRequest("settings/providers", "POST", request);
  };

  useEffect(() => {
    if (addProviderErrors && addProviderStatusCode !== 200) {
      setApiErrors(current => [...current, ...addProviderErrors]);
      setShowExceptions(true);
    }
  }, [addProviderStatusCode, addProviderErrors, setApiErrors, setShowExceptions]);

  useEffect(() => {
    if (
      addProviderStatusCode === 200
      && addProviderResponseData
      && !addProviderResponseData.warningMessage
    ) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [addProviderStatusCode, addProviderResponseData]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="add-provider">
              Add Provider
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleAddProviderClick)}>
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-collectors"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsCollectors }}
                    onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  >
                    Collectors
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-provider">
                    Add Provider
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || addProviderIsSending}
                  aria-disabled={addButtonDisabled || addProviderIsSending}
                  aria-label="Create provider"
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to collectors"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="pt-2" data-testid="formElements">
              <Container fluid>
                <Row className="d-flex mt-1 ms-1">
                  <Col xs={12}>
                    <div className="control-pane">
                      <div className="control-section" />
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="name">
                          <Form.Label
                            id="nameLabel"
                            className="horiz-form-label"
                          >
                            Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "name",
                                errors,
                                null
                              )}
                              aria-labelledby="nameLabel"
                              maxLength={50}
                              {...register("name")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="name"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="type">
                          <Form.Label
                            id="typeLabel"
                            className="horiz-form-label"
                          >
                            Type:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "type",
                                errors,
                                null
                              )}
                              aria-labelledby="typeLabel"
                              maxLength={50}
                              {...register("type")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="type"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="emailAddress">
                          <Form.Label
                            id="emailAddressLabel"
                            className="horiz-form-label"
                          >
                            Email:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "emailAddress",
                                errors,
                                null
                              )}
                              aria-labelledby="emailAddressLabel"
                              maxLength={50}
                              {...register("emailAddress")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="emailAddress"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="regions">
                          <Form.Label
                            id="regionsLabel"
                            className="horiz-form-label"
                          >
                            Region(s):
                          </Form.Label>
                          <Col>
                            {regions && (
                              <DropDownTreeComponent
                                {...register("regions")}
                                ref={(d: DropDownTreeComponent) => {
                                  setDropDownTreeComponent(d);
                                }}
                                change={dropDownTreeComponentOnChange}
                                treeSettings={regionsTreeSettings}
                                fields={regionFields}
                                allowMultiSelection
                                showCheckBox
                                changeOnBlur={false}
                                mode="Box"
                                id="regionsDropDown"
                                htmlAttributes={{
                                  "data-testid": "ddlRegions",
                                  "aria-required": "true"
                                }}
                              />
                            )}
                            <NoData
                              visible={
                                !apiErrors && !getRegionsIsSending && !regions
                              }
                              description="No Regions Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="regions"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="notes">
                          <Form.Label
                            id="notesLabel"
                            className="horiz-form-label"
                          >
                            Notes:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "notes",
                                errors,
                                null
                              )}
                              aria-labelledby="notesLabel"
                              maxLength={50}
                              {...register("notes")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="notes"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2 mt-3">
                        <Form.Group
                          as={Col}
                          controlId="active"
                          className="d-flex align-items-center"
                        >
                          <Form.Label
                            id="activeLabel"
                            className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                          >
                            Active:
                          </Form.Label>
                          <Form.Check
                            {...register("active")}
                            type="switch"
                            className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                            aria-required="true"
                            aria-labelledby="activeLabel"
                            defaultChecked
                          />
                          <ErrorMessages
                            fieldName="active"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </section>
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default AddProvider;
